import { useEffect, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";

import { BackButton } from "@/blocks/BackButton";
import { CheckYourEmail } from "@/blocks/CheckYourEmail";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { SecureTextEntryToggle } from "@/blocks/SecureTextEntryToggle";
import { SupportCallButton } from "@/blocks/SupportCallButton";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import {
  useSendPasswordResetEmailMutation,
  useSignInWithEmailAndPasswordMutation,
} from "@/data/mutations/authMutations";
import { passwordFormSchema, PasswordFormType } from "@/domain/auth";
import { useTranslator } from "@/hooks/useTranslate";
import { mfaResolverAtom } from "@/stores/atoms";
import { logger } from "@/utils/logger";
import { handleMfaError } from "@/utils/mfa";
import { normalizeSearchParam } from "@/utils/routing";
import { getUsernameAuthEmail } from "@/utils/usernameAuth";

const PasswordScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const { email, username } = useLocalSearchParams<{
    email?: string;
    username?: string;
  }>();
  const normalizedUsername = normalizeSearchParam(username);
  const signInEmail = normalizedUsername
    ? getUsernameAuthEmail(normalizedUsername)
    : normalizeSearchParam(email);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPendingConfirmation, setHasPendingConfirmation] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const setMfaResolver = useSetAtom(mfaResolverAtom);

  const { mutate: signIn } = useSignInWithEmailAndPasswordMutation();
  const { mutate: sendPasswordResetEmail, isPending: isSendingResetEmail } =
    useSendPasswordResetEmailMutation();

  useEffect(() => {
    if (!signInEmail) {
      router.replace("/(unauthed)/auth");
    }
  }, [signInEmail, router]);

  const passwordForm = useForm<PasswordFormType>({
    defaultValues: { password: "" },
    resolver: standardSchemaResolver(passwordFormSchema),
  });

  const handleSubmit = (data: PasswordFormType) => {
    setIsLoading(true);
    signIn(
      { email: signInEmail, password: data.password },
      {
        onError: error => {
          if (
            handleMfaError(error, resolver => {
              setMfaResolver(resolver);
              router.push("/(unauthed)/mfa-verify");
            })
          ) {
            setIsLoading(false);
            return;
          }
          setIsLoading(false);
          logger.error({
            message: "Failed to sign in with email and password",
            error,
          });
          passwordForm.setError("password", {
            message: t("common:validation.invalidPassword"),
          });
        },
      },
    );
  };

  const handleForgotPassword = () => {
    sendPasswordResetEmail(signInEmail, {
      onSuccess: () => {
        setHasPendingConfirmation(true);
      },
      onError: error => {
        logger.error({
          message: "Failed to send password reset email",
          error,
        });
      },
    });
  };

  return (
    <ScreenLayout className="py-12">
      {!hasPendingConfirmation && (
        <VnBox className="mt-4 items-start">
          <BackButton
            textClassName="text-sidebar"
            iconClassName="text-sidebar"
            onPress={() => router.back()}
            variant="ghost"
          />
        </VnBox>
      )}
      <VnBox className="mt-safe-offset-10 web:mt-10 flex items-center px-4 sm:my-auto md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          {hasPendingConfirmation ? (
            <CheckYourEmail
              email={signInEmail}
              bodyI18nKey="native:passwordScreen.resetPasswordInstructions"
              onBackPress={() => {
                setHasPendingConfirmation(false);
              }}
            />
          ) : (
            <VnBox className="flex flex-col gap-6">
              <VnBox className="gap-2">
                <VnText
                  variant="headerOne"
                  i18nKey="native:passwordScreen.title"
                  className="text-center"
                />
                <VnText
                  variant="lg"
                  className="text-muted-foreground text-center"
                  i18nKey="native:passwordScreen.subtitle"
                />
              </VnBox>
              <VnForm form={passwordForm} isInputOnlyForm>
                <VnCard className="gap-6 rounded-2xl px-4">
                  <VnTextInput
                    control={passwordForm.control}
                    name="password"
                    labelI18nKey="native:passwordScreen.passwordInputLabel"
                    placeholder={t(
                      "native:passwordScreen.passwordInputPlaceholder",
                    )}
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry={!isPasswordVisible}
                    returnKeyType="done"
                    enablesReturnKeyAutomatically
                    onSubmitEditing={() => {
                      void passwordForm.handleSubmit(handleSubmit)();
                    }}
                    isRequired
                    rightElement={
                      <SecureTextEntryToggle
                        isVisible={isPasswordVisible}
                        onPress={() => setIsPasswordVisible(prev => !prev)}
                      />
                    }
                  />
                  <VnButton
                    onPress={() => {
                      void passwordForm.handleSubmit(handleSubmit)();
                    }}
                    i18nKey="common:continue"
                    isLoading={isLoading}
                    className="rounded-xl"
                  />
                  <VnBox className="items-center">
                    {normalizedUsername ? (
                      // A username account has no email to send a reset link to.
                      <>
                        <VnText
                          variant="sm"
                          className="text-muted-foreground text-center"
                          i18nKey="native:passwordScreen.usernameResetUnavailable"
                        />
                        <SupportCallButton
                          variant="ghost"
                          className="h-auto p-0"
                          textClassName="text-muted-foreground cursor-pointer underline"
                        />
                      </>
                    ) : (
                      <VnButton
                        variant="ghost"
                        onPress={handleForgotPassword}
                        isLoading={isSendingResetEmail}
                        className="h-auto p-0"
                        textClassName="text-muted-foreground cursor-pointer underline"
                        i18nKey="native:passwordScreen.forgotPassword"
                      />
                    )}
                  </VnBox>
                </VnCard>
              </VnForm>
            </VnBox>
          )}
        </VnBox>
      </VnBox>
    </ScreenLayout>
  );
};

export { PasswordScreen };
